import { z } from 'zod';
import { patchOf } from './patch';
import { LABEL_COLORS, PRIORITIES, REQUEST_STATUSES, SERVICE_UNITS, STATUS_CATEGORIES } from '../domain';
import { richTextSchema } from './rich-text';

const person = z.object({ id: z.uuid(), name: z.string(), avatarUrl: z.string().nullable() });
const nullableIso = z.iso.datetime({ offset: true }).nullable().optional();

// Serviços / materiais ---------------------------------------------------------

export const serviceTypeDto = z.object({
  id: z.uuid(),
  categoryId: z.uuid(),
  name: z.string(),
  unit: z.enum(SERVICE_UNITS),
  /** null para quem não vê valores. */
  priceCents: z.number().nullable(),
  estimateMinutes: z.number().nullable(),
  isActive: z.boolean(),
});
export type ServiceTypeDto = z.infer<typeof serviceTypeDto>;
export const serviceCategoryDto = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.enum(LABEL_COLORS),
  isActive: z.boolean(),
  types: z.array(serviceTypeDto),
});
export type ServiceCategoryDto = z.infer<typeof serviceCategoryDto>;

export const serviceCategoryInput = z.object({
  name: z.string().trim().min(1).max(60),
  color: z.enum(LABEL_COLORS).default('sand'),
  isActive: z.boolean().default(true),
});
export const serviceTypeInput = z.object({
  categoryId: z.uuid(),
  name: z.string().trim().min(1).max(80),
  unit: z.enum(SERVICE_UNITS).default('piece'),
  priceCents: z.number().int().min(0).max(1e12).nullable().default(null),
  estimateMinutes: z.number().int().min(0).max(100_000).nullable().default(null),
  isActive: z.boolean().default(true),
});
export type ServiceTypeInput = z.input<typeof serviceTypeInput>;

// Workflow ---------------------------------------------------------------------

export const workflowStatusDto = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.enum(LABEL_COLORS),
  category: z.enum(STATUS_CATEGORIES),
  rank: z.string(),
  wipLimit: z.number().nullable(),
  isClientVisible: z.boolean(),
});
export type WorkflowStatusDto = z.infer<typeof workflowStatusDto>;
export const workflowDto = z.object({
  id: z.uuid(),
  name: z.string(),
  isDefault: z.boolean(),
  projectId: z.uuid().nullable(),
  statuses: z.array(workflowStatusDto),
});
export type WorkflowDto = z.infer<typeof workflowDto>;

// Tarefas ------------------------------------------------------------------------

export const taskInput = z.object({
  contractId: z.uuid().nullable().optional(),
  title: z.string().trim().min(1, 'Dê um título à tarefa').max(300),
  projectId: z.uuid().nullable().optional(),
  clientId: z.uuid().nullable().optional(),
  parentTaskId: z.uuid().nullable().optional(),
  statusId: z.uuid().optional(),
  priority: z.enum(PRIORITIES).default('medium'),
  description: richTextSchema.nullable().optional(),
  startAt: nullableIso,
  internalDueAt: nullableIso,
  clientDueAt: nullableIso,
  estimateMinutes: z.number().int().min(0).max(100_000).nullable().optional(),
  valueCents: z.number().int().min(0).max(1e12).nullable().optional(),
  responsibleIds: z.array(z.uuid()).max(20).optional(),
  collaboratorIds: z.array(z.uuid()).max(30).optional(),
  tagIds: z.array(z.uuid()).max(30).optional(),
  items: z
    .array(z.object({ serviceTypeId: z.uuid(), quantity: z.number().int().min(1).max(10_000).default(1) }))
    .max(50)
    .optional(),
});
export type TaskInput = z.input<typeof taskInput>;

/** Edição parcial: só os campos enviados mudam. */
export const taskPatch = patchOf(
  taskInput.omit({ parentTaskId: true, items: true, responsibleIds: true, collaboratorIds: true }),
);
export type TaskPatch = z.input<typeof taskPatch>;

export const taskMoveInput = z.object({
  statusId: z.uuid(),
  beforeId: z.uuid().nullable().optional(),
  afterId: z.uuid().nullable().optional(),
});
export const assigneesInput = z.object({
  responsibleIds: z.array(z.uuid()).max(20),
  collaboratorIds: z.array(z.uuid()).max(30),
});

export const taskItemDto = z.object({
  id: z.uuid(),
  serviceTypeId: z.uuid(),
  name: z.string(),
  categoryName: z.string(),
  quantity: z.number(),
  unitPriceCents: z.number().nullable(),
});
export type TaskItemDto = z.infer<typeof taskItemDto>;
export const taskItemInput = z.object({
  serviceTypeId: z.uuid(),
  quantity: z.number().int().min(1).max(10_000).default(1),
  unitPriceCents: z.number().int().min(0).nullable().optional(),
});

export const taskSummaryDto = z.object({
  contractId: z.uuid().nullable().optional(),
  id: z.uuid(),
  title: z.string(),
  priority: z.enum(PRIORITIES),
  status: workflowStatusDto,
  project: z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) }).nullable(),
  client: z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) }).nullable(),
  parentTaskId: z.uuid().nullable(),
  responsibles: z.array(person),
  collaborators: z.array(person),
  startAt: z.string().nullable(),
  internalDueAt: z.string().nullable(),
  clientDueAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  /** Materiais resumidos ("3 Feed, 1 Reel"). */
  itemsLabel: z.string(),
  subtaskCount: z.number(),
  subtaskDone: z.number(),
  checklistCount: z.number(),
  checklistDone: z.number(),
  commentCount: z.number(),
  attachmentCount: z.number(),
  tags: z.array(z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) })),
  rank: z.string(),
  updatedAt: z.string(),
});
export type TaskSummaryDto = z.infer<typeof taskSummaryDto>;

export const checklistItemDto = z.object({
  id: z.uuid(),
  text: z.string(),
  done: z.boolean(),
  rank: z.string(),
});
export type ChecklistItemDto = z.infer<typeof checklistItemDto>;
export const taskLinkDto = z.object({ id: z.uuid(), url: z.string(), title: z.string().nullable() });

export const taskDto = taskSummaryDto.extend({
  description: richTextSchema.nullable(),
  estimateMinutes: z.number().nullable(),
  valueCents: z.number().nullable(),
  items: z.array(taskItemDto),
  subtasks: z.array(taskSummaryDto),
  checklist: z.array(checklistItemDto),
  dependencies: z.array(z.object({ id: z.uuid(), title: z.string(), done: z.boolean() })),
  links: z.array(taskLinkDto),
  sourceRequestId: z.uuid().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  can: z.object({ update: z.boolean(), delete: z.boolean() }),
});
export type TaskDto = z.infer<typeof taskDto>;

export const taskListQuery = z.object({
  projectId: z.uuid().optional(),
  clientId: z.uuid().optional(),
  parentId: z.union([z.uuid(), z.literal('none')]).optional(),
  assignee: z.union([z.literal('me'), z.uuid()]).optional(),
  /** Prazo interno: atrasadas, hoje, próximos 7 dias. */
  due: z.enum(['overdue', 'today', 'week', 'none']).optional(),
  open: z.enum(['1']).optional(),
  q: z.string().trim().max(120).optional(),
});

export const checklistInput = z.object({ text: z.string().trim().min(1).max(300) });
export const checklistPatch = z.object({
  text: z.string().trim().min(1).max(300).optional(),
  done: z.boolean().optional(),
});
export const linkInput = z.object({
  url: z.url('Link inválido').refine((u) => /^https?:/i.test(u), 'Use um link http(s)'),
  title: z.string().trim().max(160).optional(),
});
export const dependencyInput = z.object({ dependsOnTaskId: z.uuid() });

// Comentários --------------------------------------------------------------------

export const commentDto = z.object({
  id: z.uuid(),
  author: person.nullable(),
  authorName: z.string(),
  body: richTextSchema,
  visibility: z.enum(['internal', 'client']),
  parentId: z.uuid().nullable(),
  /** Âncora de revisão (Fase 15): ponto na imagem ou tempo no vídeo. */
  anchor: z.record(z.string(), z.unknown()).nullable(),
  pinNumber: z.number().nullable(),
  status: z.enum(['open', 'resolved']),
  editedAt: z.string().nullable(),
  createdAt: z.string(),
  mine: z.boolean(),
});
export type CommentDto = z.infer<typeof commentDto>;
/** Âncora de revisão (SPEC §22 e §23): ponto na imagem (0 a 1) ou instante do vídeo (ms), por versão. */
export const reviewAnchor = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('pin'),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    versionId: z.uuid().optional(),
  }),
  z.object({
    type: z.literal('time'),
    t: z.number().int().min(0).max(36_000_000),
    versionId: z.uuid().optional(),
  }),
]);
export type ReviewAnchor = z.infer<typeof reviewAnchor>;

export const commentInput = z.object({
  body: richTextSchema,
  visibility: z.enum(['internal', 'client']).default('internal'),
  parentId: z.uuid().nullable().optional(),
  anchor: reviewAnchor.nullable().optional(),
});

// Inbox (solicitações) -----------------------------------------------------------

export const requestInput = z.object({
  quantity: z.number().int().min(1).max(100000).default(1),
  title: z.string().trim().min(1, 'Dê um título ao pedido').max(200),
  clientId: z.uuid().nullable().optional(),
  projectId: z.uuid().nullable().optional(),
  serviceTypeId: z.uuid().nullable().optional(),
  description: z.string().trim().max(20_000).optional(),
  objective: z.string().trim().max(5_000).optional(),
  requestedDueAt: z.iso.date().nullable().optional(),
  priority: z.enum(PRIORITIES).default('medium'),
  notes: z.string().trim().max(5_000).optional(),
  references: z.array(z.url()).max(20).default([]),
  requesterName: z.string().trim().max(120).optional(),
});
export type RequestInput = z.input<typeof requestInput>;

export const requestDto = z.object({
  quantity: z.number(),
  id: z.uuid(),
  title: z.string(),
  status: z.enum(REQUEST_STATUSES),
  priority: z.enum(PRIORITIES),
  client: z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) }).nullable(),
  project: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  serviceType: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  description: z.string().nullable(),
  objective: z.string().nullable(),
  notes: z.string().nullable(),
  references: z.array(z.string()),
  requestedDueAt: z.string().nullable(),
  requesterName: z.string(),
  requestedBy: person.nullable(),
  resolutionTaskId: z.uuid().nullable(),
  resolutionProjectId: z.uuid().nullable(),
  resolutionNote: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  attachmentCount: z.number(),
  createdAt: z.string(),
});
export type RequestDto = z.infer<typeof requestDto>;

export const convertRequestInput = z.discriminatedUnion('mode', [
  /** Vira tarefa (avulsa ou num projeto existente). */
  z.object({
    mode: z.literal('task'),
    contractId: z.uuid().nullable().optional(),
    confirmedDueAt: z.iso.datetime().nullable().optional(),
    projectId: z.uuid().nullable().optional(),
    responsibleIds: z.array(z.uuid()).default([]),
  }),
  /** Cria um projeto novo com a tarefa dentro. */
  z.object({
    mode: z.literal('project'),
    contractId: z.uuid().nullable().optional(),
    confirmedDueAt: z.iso.datetime().nullable().optional(),
    projectName: z.string().trim().min(1).max(160),
    responsibleIds: z.array(z.uuid()).default([]),
  }),
]);
export const resolveRequestInput = z.object({ note: z.string().trim().max(2000).optional() });

// Workflow (edição) ----------------------------------------------------------------

export const statusInput = z.object({
  name: z.string().trim().min(1, 'Dê um nome à etapa').max(40),
  color: z.enum(LABEL_COLORS),
  category: z.enum(STATUS_CATEGORIES),
  wipLimit: z.number().int().min(1).max(999).nullable().default(null),
  isClientVisible: z.boolean().default(true),
});
export type StatusInput = z.input<typeof statusInput>;
export const statusMoveInput = z.object({
  beforeId: z.uuid().nullable().optional(),
  afterId: z.uuid().nullable().optional(),
});
export const deleteStatusInput = z.object({ moveTasksTo: z.uuid() });
export const projectWorkflowInput = z.object({ mode: z.enum(['default', 'custom']) });

/** Evento de tempo real: só identificadores, nunca conteúdo. O cliente recarrega o que precisa. */
export const realtimeEvent = z.object({
  action: z.string(),
  entityType: z.string(),
  entityId: z.uuid(),
  projectId: z.uuid().nullable(),
  clientId: z.uuid().nullable(),
  actorUserId: z.uuid().nullable(),
});
export type RealtimeEvent = z.infer<typeof realtimeEvent>;
